import * as z from "zod";

/** Mirrored in FrontendMetricsController::PROOF_LANDING_SOURCES. */
const ProofLandingRef = z.enum(["proof_github_report", "codesmith_check"]);

type ProofLandingSource = z.infer<typeof ProofLandingRef> | "none" | "other";

export interface ProofLanding {
  source: ProofLandingSource;
  place: string;
  /** The `proof-report:<child>` pane's child; null for a PR workspace `proof:` pane until its report renders. */
  reportId: string | null;
}

// Literal prefixes: this runs in the root bundle, which proofReportArtifactId's imports would outgrow.
const PROOF_REPORT_PREFIX = "proof-report:";
const PROOF_PANE_PREFIXES = [PROOF_REPORT_PREFIX, "proof:"];

/** `proof-report:<child>` or the PR workspace's `proof:<owner>/<repo>#<n>`, which the app rewrites the former to after load. */
export function isProofPane(pane: string | null): boolean {
  return (
    pane !== null &&
    PROOF_PANE_PREFIXES.some((prefix) => pane.startsWith(prefix))
  );
}

/**
 * The page a path shows, the same across OrgRedirect's org-less session redirect:
 * `/sessions/s1` and `/acme/sessions/s1` → `"session:s1"`; any other path is itself.
 */
export function landingPlace(pathname: string): string {
  const session = /\/sessions\/([^/]+)/.exec(pathname);
  return session ? `session:${session[1]}` : pathname;
}

/**
 * The Proof report the document's own navigation landed on, and where from:
 * `/acme/sessions/s1?pane=proof-report%3Ac1&ref=codesmith_check` →
 * `{ source: "codesmith_check", place: "session:s1", reportId: "c1" }`.
 * No `ref` is `"none"` and any other `ref` `"other"`. Null when the load did not open a Proof report.
 */
export function hardLoadProofLanding(): ProofLanding | null {
  const [navigation] = performance.getEntriesByType?.("navigation") ?? [];
  if (!navigation) return null;
  let url: URL;
  try {
    url = new URL(navigation.name);
  } catch {
    return null;
  }
  const pane = url.searchParams.get("pane");
  if (!isProofPane(pane)) return null;
  return {
    source: landingSource(url.searchParams.get("ref")),
    place: landingPlace(url.pathname),
    reportId: proofReportChild(pane),
  };
}

/** `proof-report:c1` and the round link `proof-report:c1:r2` → `"c1"`; other panes → null. */
export function proofReportChild(pane: string | null): string | null {
  if (!pane?.startsWith(PROOF_REPORT_PREFIX)) return null;
  return pane.slice(PROOF_REPORT_PREFIX.length).split(":")[0] ?? null;
}

function landingSource(ref: string | null): ProofLandingSource {
  if (!ref) return "none";
  const known = ProofLandingRef.safeParse(ref);
  return known.success ? known.data : "other";
}
